import React, {
  useCallback,
  useContext,
  useState,
  Suspense,
  useLayoutEffect,
  useEffect,
} from 'react';
import {
  Platform,
  Pressable,
  Text,
  View,
  ActivityIndicator,
  Dimensions,
  useWindowDimensions,
} from 'react-native';
import { Menu } from 'react-native-paper';
import { AppAlert } from '../services/AppAlert';
import appAnalytics from '../services/appAnalytics';
import { buildAppAnalyticsContext } from '../services/appAnalyticsContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  createNativeStackNavigator,
  NativeStackScreenProps,
} from '@react-navigation/native-stack';
import { NavigatorScreenParams } from '@react-navigation/native';

// Eager imports for critical path
import MainScreenTabNavigator from '../navigation/TabNavigator/MainScreenTabNavigator';
import DrawerNavigation from '../navigation/DrawerNavigator/DrawerNavigation';
// Eager: initial route for eligible new owners; Home must never flash first.
import OwnerSetupWizardScreen from '../screens/onboarding/OwnerSetupWizardScreen';
import HeaderLeft from '../components/Reusables/headers/HeaderLeft';
import OrdersTabNavigator from '../navigation/TabNavigator/OrdersScreenTabNavigator';
import { BackOfficeTabNavigator } from './backoffice';
import BackOfficeEmployeesScreen from '../screens/backoffice/BackOfficeEmployeesScreen';
import BackOfficeCustomersScreen from '../screens/backoffice/BackOfficeCustomersScreen';
import BackOfficeReportsScreen from '../screens/backoffice/BackOfficeReportsScreen';
import BackOfficeIntegrationsScreen from '../screens/backoffice/BackOfficeIntegrationsScreen';
import BackOfficeSuppliersScreen from '../screens/backoffice/BackOfficeSuppliersScreen';
import BackOfficeAddSupplierScreen from '../screens/backoffice/BackOfficeAddSupplierScreen';
import BackOfficeSupplierDetailsScreen from '../screens/backoffice/BackOfficeSupplierDetailsScreen';
import BackOfficePurchaseOrderUpsertScreen from '../screens/backoffice/BackOfficePurchaseOrderUpsertScreen';
import BackOfficePurchaseOrderDetailsScreen from '../screens/backoffice/BackOfficePurchaseOrderDetailsScreen';
import BackOfficeAddInvoiceScreen from '../screens/backoffice/BackOfficeAddInvoiceScreen';
import BackOfficeCreateGoodsReceiptScreen from '../screens/backoffice/BackOfficeCreateGoodsReceiptScreen';

import moment from 'moment';
import { useDispatch } from 'react-redux';
import { setUpdatingCartitems } from '../redux/cartSlice';
import { PrintReceiptContext } from '../Context/PrintReceiptContext';
import Zocial from '@react-native-vector-icons/zocial/static';
import { prioritizeProductsByProductObject } from '../redux/productSlice';
import {
  addPreviouspendingOrder,
  clearFilteredPendingOrder,
  clearFilteredCompletedOrder,
  clearPendingOrders,
  clearCompletedOrders,
  clearPartialOrders,
  clearFilteredPartialOrder,
} from '../redux/OrdersSlice';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { ExtrasContext } from '../Context/ExtrasContext';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
// On web, this import resolves to `AppSearchBar.web.tsx` via resolver extensions.
import AppSearchBar from '../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../components/Reusables/AppSearchBar.web';
import SearchContext from '../Context/SearchContext';
import Typography from '../components/Reusables/Typography';
import { isTablet } from 'react-native-device-info';
import { fs, iconSize, s, v } from '../utils/libs';
import { saleLineUnitPrice } from '../utils/backofficeSaleLines';
import { isEmployeeAdminRole } from '../utils/roles';
import NetworkMonitor from '../components/NetworkMonitor';
import { useTheme, GTFonts, useLayoutSize } from '../theme';
import DateRangeDropdown from '../components/Reusables/headers/DateRangeDropdown';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { useAppSelector } from '../redux/helper';
import { BackOfficeTabParamList, DrawerParamList } from './types';
import {
  matchesAuthenticatedOwner,
  selectShouldEnterInitialOwnerSetup,
  type SetupWizardStepId,
} from '../redux/ownerActivationSlice';
import { isEmployeeOwnerRole } from '../utils/roles';
import BackOfficeHeaderLocationPicker from '../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import WebScreenShell from '../components/web/WebScreenShell';
import BackOfficeScreenShell from '../components/web/BackOfficeScreenShell';
import WebHeaderBar, {
  type WebHeaderBarProps,
} from '../components/web/WebHeaderBar';
import HeaderIconButton from '../components/Reusables/headers/HeaderIconButton';
import HeaderInternetStatusIcon from '../components/Reusables/headers/HeaderInternetStatusIcon';
import HeaderActions from '../components/Reusables/headers/HeaderActions';
import { BackOfficeSessionProvider } from '../Context/BackOfficeSessionContext';
import { shouldShowTabletSidebarLayoutSetting } from '../utils/layoutUtils';
import {
  getStockValueTitle,
  type InventoryFilters,
} from '../utils/inventoryFilters';

// Lazy imports
const Cart = React.lazy(() => import('../screens/CartScreen'));
const Checkout = React.lazy(() =>
  import('../screens/checkout/CheckoutScreen').then(module => ({
    default: module.Checkout,
  })),
);
const OrderDetails = React.lazy(() => import('../screens/orders/OrderDetails'));
const NotesScreen = React.lazy(
  () => import('../screens/notes/notesScreen/NotesScreen'),
);
const NewNoteScreen = React.lazy(
  () => import('../screens/notes/notesScreen/components/NewNoteScreen'),
);
const ViewNoteScreen = React.lazy(
  () => import('../screens/notes/notesScreen/components/ViewNoteScreen'),
);
const SettingsScreen = React.lazy(
  () => import('../screens/settings/SettingsScreen'),
);
const GettingStartedHubScreen = React.lazy(
  () => import('../screens/settings/GettingStartedHubScreen'),
);
const SecuritySettingsScreen = React.lazy(
  () => import('../screens/settings/SecuritySettingsScreen'),
);
const ProductSettingsScreen = React.lazy(
  () => import('../screens/settings/ProductSettingsScreen'),
);
const CheckoutSettingsScreen = React.lazy(
  () => import('../screens/settings/CheckoutSettingsScreen'),
);
const CameraSettingsScreen = React.lazy(
  () => import('../screens/settings/CameraSettingsScreen'),
);
const PartialOrdersSelectItems = React.lazy(
  () => import('../screens/checkout/PartialOrdersSelectItems'),
);
const PartialOrdersCheckoutScreen = React.lazy(
  () => import('../screens/checkout/PartialOrdersCheckoutScreen'),
);
const FaqScreen = React.lazy(() => import('../screens/faq/FaqScreen'));
const NewProduct = React.lazy(
  () => import('../screens/products/newproducts/NewProduct'),
);
const PrinterSettingsScreen = React.lazy(
  () => import('../screens/settings/PrinterSettingsScreen'),
);
const PrinterListScreen = React.lazy(
  () => import('../screens/settings/PrinterListScreen'),
);
const PrinterProfileEditorScreen = React.lazy(
  () => import('../screens/settings/PrinterProfileEditorScreen'),
);
const ProductScanner = React.lazy(
  () => import('../screens/productscanner/ProductScanner'),
);
const BarcodeCapture = React.lazy(
  () => import('../screens/productscanner/BarcodeCapture'),
);
const EmployeeSelfieScreen = React.lazy(
  () => import('../screens/employee/EmployeeSelfieScreen'),
);
const NewLocation = React.lazy(() => import('../screens/location/NewLocation'));
const ServiceDisplayScreen = React.lazy(
  () => import('../screens/service/ServiceDisplayScreen'),
);
const QuickActionsSettings = React.lazy(
  () => import('../screens/settings/QuickActionsSettings'),
);
const HomePageSettings = React.lazy(
  () => import('../screens/settings/HomePageSettings'),
);
const NoInternetScreen = React.lazy(
  () => import('../screens/NoInternetScreen'),
);
const SuggestionScreen = React.lazy(
  () => import('../screens/feedback/SuggestionScreen'),
);
const BackOfficeSummaryScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeSummaryScreen'),
);
const BackOfficeReportPage = React.lazy(
  () => import('../screens/backoffice/BackOfficeReportPage'),
);
const BackOfficeCompanyProfileScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeCompanyProfileScreen'),
);
const BackOfficeBillingScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeBillingScreen'),
);
const BackOfficeSupportScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeSupportScreen'),
);
const BackOfficeDangerZoneScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeDangerZoneScreen'),
);
const BackOfficeUserProfileScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeUserProfileScreen'),
);
const BackOfficeProfilePhotoScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeProfilePhotoScreen'),
);
const BackOfficeEmployeeDetailsScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeEmployeeDetailsScreen'),
);
const BackOfficeEmployeeViewScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeEmployeeViewScreen'),
);
const BackOfficeProductDetailsScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeProductDetailsScreen'),
);
const BackOfficeProductViewScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeProductViewScreen'),
);
const BackOfficeVariantViewScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeVariantViewScreen'),
);
const BackOfficeStockValueScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeStockValueScreen'),
);
const BackOfficeOrderDetailsScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeOrderDetailsScreen'),
);
const BackOfficeAddProductScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeAddProductScreen'),
);
const BackOfficeAddEmployeeScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeAddEmployeeScreen'),
);
const BackOfficeCustomerDetailsScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeCustomerDetailsScreen'),
);
const BackOfficeCustomerOrdersScreen = React.lazy(
  () =>
    import(
      '../screens/backoffice/customerOrders/BackOfficeCustomerOrdersScreen'
    ),
);
const BackOfficeAddCustomerScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeAddCustomerScreen'),
);
const BackOfficeExpenseCategoriesScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeExpenseCategoriesScreen'),
);
const BackOfficeAddExpenseScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeAddExpenseScreen'),
);
const BackOfficeExpenseDetailScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeExpenseDetailScreen'),
);
const BackOfficeLatestExpensesScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficeLatestExpensesScreen'),
);
const BackOfficePaymentBreakdownScreen = React.lazy(
  () => import('../screens/backoffice/BackOfficePaymentBreakdownScreen'),
);
const CalculatorScreen = React.lazy(
  () => import('../screens/utility/CalculatorScreen'),
);
const MpesaIntegrationScreen = React.lazy(
  () => import('../screens/backoffice/MpesaIntegrationScreen'),
);
const MpesaIntegrationConnectScreen = React.lazy(
  () => import('../screens/backoffice/MpesaIntegrationConnectScreen'),
);
const MpesaActivationDetailsScreen = React.lazy(
  () => import('../screens/backoffice/MpesaActivationDetailsScreen'),
);
const MpesaActivationAuthoriseScreen = React.lazy(
  () => import('../screens/backoffice/MpesaActivationAuthoriseScreen'),
);
const MpesaPaymentsToCheckScreen = React.lazy(
  () => import('../screens/backoffice/MpesaPaymentsToCheckScreen'),
);
const MpesaPaymentToCheckDetailsScreen = React.lazy(
  () => import('../screens/backoffice/MpesaPaymentToCheckDetailsScreen'),
);
const CartSettingsScreen = React.lazy(
  () => import('../screens/settings/CartSettingsScreen'),
);
const OrderSettingsScreen = React.lazy(
  () => import('../screens/settings/OrderSettingsScreen'),
);
const ReportSettingsScreen = React.lazy(
  () => import('../screens/settings/ReportSettingsScreen'),
);
const NotificationsScreen = React.lazy(
  () => import('../screens/notifications/NotificationsScreen'),
);

const FallbackScreen = () => (
  <View
    style={{
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#fff',
    }}></View>
);

// Memoized wrapper for Lazy Screens - caches wrapped components to:
// 1. Return stable references (prevents remounting on parent re-render)
const suspenseCache = new Map<
  React.ComponentType<any>,
  React.ComponentType<any>
>();
const withSuspense = (Component: React.ComponentType<any>) => {
  const cached = suspenseCache.get(Component);
  if (cached) return cached;

  const Wrapped = (props: any) => (
    <Suspense fallback={<FallbackScreen />}>
      <Component {...props} />
    </Suspense>
  );
  Wrapped.displayName = `withSuspense(${Component.displayName || Component.name || 'Component'})`;
  suspenseCache.set(Component, Wrapped);
  return Wrapped;
};

const CartWithSuspense = withSuspense(Cart);
const CheckoutWithSuspense = withSuspense(Checkout);
const PartialOrdersCheckoutScreenWithSuspense = withSuspense(
  PartialOrdersCheckoutScreen as React.ComponentType<any>,
);
const PartialOrdersSelectItemsWithSuspense = withSuspense(
  PartialOrdersSelectItems,
);
const ProductScannerWithSuspense = withSuspense(ProductScanner);
const CalculatorWithSuspense = withSuspense(CalculatorScreen);
const SettingsWithSuspense = withSuspense(SettingsScreen);
const GettingStartedHubWithSuspense = withSuspense(GettingStartedHubScreen);
const SecuritySettingsWithSuspense = withSuspense(SecuritySettingsScreen);
const CameraSettingsScreenWithSuspense = withSuspense(CameraSettingsScreen);
const ProductSettingsScreenWithSuspense = withSuspense(ProductSettingsScreen);
const CheckoutSettingsScreenWithSuspense = withSuspense(CheckoutSettingsScreen);
const OrderSettingsScreenWithSuspense = withSuspense(OrderSettingsScreen);
const ReportSettingsScreenWithSuspense = withSuspense(ReportSettingsScreen);
const NotificationsScreenWithSuspense = withSuspense(NotificationsScreen);
const PrinterSettingsScreenWithSuspense = withSuspense(PrinterSettingsScreen);
const PrinterListScreenWithSuspense = withSuspense(PrinterListScreen);
const PrinterProfileEditorScreenWithSuspense = withSuspense(
  PrinterProfileEditorScreen,
);
const QuickActionsSettingsWithSuspense = withSuspense(QuickActionsSettings);
const HomePageSettingsWithSuspense = withSuspense(HomePageSettings);
const CartSettingsScreenWithSuspense = withSuspense(CartSettingsScreen);
const BackOfficeExpenseCategoriesWithSuspense = withSuspense(
  BackOfficeExpenseCategoriesScreen,
);
const BackOfficeAddExpenseWithSuspense = withSuspense(
  BackOfficeAddExpenseScreen,
);
const BackOfficeExpenseDetailWithSuspense = withSuspense(
  BackOfficeExpenseDetailScreen,
);
const BackOfficeLatestExpensesWithSuspense = withSuspense(
  BackOfficeLatestExpensesScreen,
);
const BackOfficePaymentBreakdownWithSuspense = withSuspense(
  BackOfficePaymentBreakdownScreen,
);
const BackOfficeReportPageWithSuspense = withSuspense(BackOfficeReportPage);
const MpesaIntegrationWithSuspense = withSuspense(MpesaIntegrationScreen);
const MpesaIntegrationConnectWithSuspense = withSuspense(
  MpesaIntegrationConnectScreen,
);
const MpesaActivationDetailsWithSuspense = withSuspense(
  MpesaActivationDetailsScreen,
);
const MpesaActivationAuthoriseWithSuspense = withSuspense(
  MpesaActivationAuthoriseScreen,
);
const MpesaPaymentsToCheckWithSuspense = withSuspense(
  MpesaPaymentsToCheckScreen,
);
const MpesaPaymentToCheckDetailsWithSuspense = withSuspense(
  MpesaPaymentToCheckDetailsScreen,
);
const BackOfficeEmployeeDetailsWithSuspense = withSuspense(
  BackOfficeEmployeeDetailsScreen,
);
const BackOfficeEmployeeViewWithSuspense = withSuspense(
  BackOfficeEmployeeViewScreen,
);
const BackOfficeProductDetailsWithSuspense = withSuspense(
  BackOfficeProductDetailsScreen,
);
const BackOfficeProductViewWithSuspense = withSuspense(
  BackOfficeProductViewScreen,
);
const BackOfficeVariantViewWithSuspense = withSuspense(
  BackOfficeVariantViewScreen,
);
const BackOfficeStockValueWithSuspense = withSuspense(
  BackOfficeStockValueScreen,
);
const BackOfficeOrderDetailsWithSuspense = withSuspense(
  BackOfficeOrderDetailsScreen,
);
const SuggestionScreenWithSuspense = withSuspense(SuggestionScreen);
const BarcodeCaptureWithSuspense = withSuspense(BarcodeCapture);
const EmployeeSelfieScreenWithSuspense = withSuspense(EmployeeSelfieScreen);
const NewLocationWithSuspense = withSuspense(NewLocation);
const ServiceDisplayScreenWithSuspense = withSuspense(ServiceDisplayScreen);
const OrderDetailsWithSuspense = withSuspense(OrderDetails);
const NotesScreenWithSuspense = withSuspense(NotesScreen);
const NewNoteScreenWithSuspense = withSuspense(NewNoteScreen);
const FaqScreenWithSuspense = withSuspense(FaqScreen);
const NewProductWithSuspense = withSuspense(NewProduct);
const EmployeeAddProductImageWithSuspense = withSuspense(
  require('../screens/products/newproducts/EmployeeAddProductImage').default,
);
const ViewNoteScreenWithSuspense = withSuspense(ViewNoteScreen);
const BackOfficeSummaryScreenWithSuspense = withSuspense(
  BackOfficeSummaryScreen,
);
const BackOfficeSupportScreenWithSuspense = withSuspense(
  BackOfficeSupportScreen,
);
const BackOfficeDangerZoneScreenWithSuspense = withSuspense(
  BackOfficeDangerZoneScreen,
);
const BackOfficeAddEmployeeScreenWithSuspense = withSuspense(
  BackOfficeAddEmployeeScreen,
);
const BackOfficeCustomerDetailsWithSuspense = withSuspense(
  BackOfficeCustomerDetailsScreen,
);
const BackOfficeCustomerOrdersWithSuspense = withSuspense(
  BackOfficeCustomerOrdersScreen,
);
const BackOfficeAddCustomerScreenWithSuspense = withSuspense(
  BackOfficeAddCustomerScreen,
);
const BackOfficeAddProductScreenWithSuspense = withSuspense(
  BackOfficeAddProductScreen,
);
const BackOfficeCompanyProfileScreenWithSuspense = withSuspense(
  BackOfficeCompanyProfileScreen,
);
const BackOfficeUserProfileScreenWithSuspense = withSuspense(
  BackOfficeUserProfileScreen,
);
const BackOfficeProfilePhotoScreenWithSuspense = withSuspense(
  BackOfficeProfilePhotoScreen,
);
const BackOfficeBillingScreenWithSuspense = withSuspense(
  BackOfficeBillingScreen,
);
export type RootStackParamList = {
  DrawerNavigation: NavigatorScreenParams<DrawerParamList> | undefined;
  OwnerSetupWizard:
    | {
        mode?: 'initial' | 'resume' | 'replay';
        initialStepId?: SetupWizardStepId;
      }
    | undefined;
  GettingStartedHub: undefined;
  MainScreen: {
    screen: string;
    params: { from: string; refreshFunction?: () => void };
  };
  Browser: undefined;
  ProductScanner: { refreshScanner?: () => void };
  /** `skipProductForm`: only emit BARCODE_SCANNED, don't fill the product form. */
  BarcodeCapture: { skipProductForm?: boolean } | undefined;
  Cart: undefined;
  CartSettingsScreen: undefined;
  Orders: undefined;
  OrderDetails: undefined;
  Notes: undefined;
  NewNote: undefined;
  ViewNote: { noteId: string; setIsDeleting?: (value: boolean) => void };
  Checkout: { order: any[] };
  SettingsScreen: undefined;
  SecuritySettingsScreen: undefined;
  ProductSettingsScreen: undefined;
  CheckoutSettingsScreen: undefined;
  OrderSettingsScreen: undefined;
  ReportSettingsScreen: undefined;
  PrinterSettingsScreen: undefined;
  AddPrinterScreen: undefined;
  EditPrinterScreen: { profileId: string };
  PartialOrdersSelectItems: { order: any[] };
  PartialOrdersCheckoutScreen: { order: any[] };
  FAQScreen: undefined;
  NewProduct:
    | {
        source?: 'activation_tracker' | 'new_product' | 'default' | 'deeplink';
        presentation?: 'simplified' | 'full';
      }
    | undefined;
  CameraSettingsScreen: undefined;
  EmployeeSelfie: undefined;
  NewLocation: undefined;
  ServiceDisplay: undefined;
  QuickActionsSettings: undefined;
  HomePageSettings: undefined;
  NoInternetScreen: undefined;
  Notifications: { notificationId?: string } | undefined;
  Suggestion: undefined;
  BackOffice: undefined | NavigatorScreenParams<BackOfficeTabParamList>;
  BackOfficeSummary: undefined;
  BackOfficeEmployees: undefined;
  BackOfficeCustomers: undefined;
  BackOfficeCustomerDetails: { customerId: string } | undefined;
  BackOfficeCustomerOrders:
    | {
        customerId: string;
        customerName?: string;
        initialTab?: 'completed' | 'pending' | 'partial';
      }
    | undefined;
  BackOfficeAddCustomer: { customerId?: string } | undefined;
  BackOfficeReports: undefined;
  BackOfficeReportPage: { reportId: string; title?: string } | undefined;
  BackOfficeBilling: undefined;
  BackOfficeSupport: undefined;
  /** Owner-only: delete this location's activity or expenses. */
  BackOfficeDangerZone: undefined;
  BackOfficeCompanyProfile: undefined;
  BackOfficeUserProfile: undefined;
  BackOfficeProfilePhoto:
    | { firstName?: string; lastName?: string; email?: string; number?: string }
    | undefined;
  /** Read-only Employee Details, opened from People › Staff. */
  BackOfficeEmployeeView: { employeeId: string } | undefined;
  /** The employee form (opened from Employee Details' Edit). */
  BackOfficeEmployeeDetails: { employeeId: string } | undefined;
  /** The product form (opened from Product Details' Edit). */
  BackOfficeProductDetails: { productId: string } | undefined;
  /** Read-only Product Details, opened from Inventory. */
  BackOfficeProductView: { productId: string } | undefined;
  BackOfficeVariantView: { productId: string; variantId: string } | undefined;
  /**
   * Stock value by category, opened from Inventory's strip: the whole store,
   * or the products the list's `filters` select.
   */
  BackOfficeStockValue: { filters?: InventoryFilters } | undefined;
  BackOfficeOrderDetails: { orderId: string } | undefined;
  BackOfficeSuppliers: undefined;
  BackOfficeAddSupplier: { supplierId?: string } | undefined;
  BackOfficeSupplierDetails: { supplierId: string } | undefined;
  BackOfficePurchaseOrderUpsert:
    | { supplierId?: string; purchaseOrderId?: string }
    | undefined;
  BackOfficePurchaseOrderDetails:
    | { supplierId: string; purchaseOrderId: string }
    | undefined;
  BackOfficeAddInvoice:
    | { supplierId?: string; purchaseOrderId?: string }
    | undefined;
  BackOfficeCreateGoodsReceipt:
    | { purchaseOrderId?: string; supplierId?: string }
    | undefined;
  BackOfficeAddProduct: undefined;
  BackOfficeAddEmployee: undefined;
  EmployeeAddProductImage: { productId: string } | undefined;
  BackOfficeExpenseCategories: undefined;
  BackOfficeAddExpense: { category: string } | undefined;
  BackOfficeExpenseDetail:
    | {
        expenseId: string;
        expense?: any;
        selectedRange?: string;
      }
    | undefined;
  BackOfficeLatestExpenses: undefined;
  BackOfficePaymentBreakdown: undefined;
  Calculator: undefined;
  BackOfficeIntegrations: undefined;
  /** `openTestPrompt`: open "Test my M-Pesa" straight away (after saving keys). */
  MpesaIntegration: { openTestPrompt?: boolean } | undefined;
  /** `edit`: PATCH an existing own-keys config (or finish one after a mode switch). */
  MpesaIntegrationConnect: { mode?: 'create' | 'edit' } | undefined;
  MpesaActivationDetails: undefined;
  MpesaActivationAuthorise: undefined;
  /** Owner: M-Pesa prompts to settle from the statement, then one of them. */
  MpesaPaymentsToCheck: undefined;
  MpesaPaymentToCheckDetails: { stkRequestId: string };
};

const Stack = createNativeStackNavigator<RootStackParamList>();

const formatLiveHeaderTime = (date: Date) =>
  moment(date).format('MMM Do YYYY, h:mm:ss a');

const getHeaderCustomerName = (name?: string) => {
  const trimmedName = String(name || '').trim();
  return trimmedName || 'Walk in customer';
};

const LiveHeaderTitle: React.FC<{ title: string }> = ({ title }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <View
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: s(260),
      }}>
      <Text
        numberOfLines={1}
        style={{
          color: 'white',
          fontFamily: GTFonts.regular,
          fontSize: fs(Platform.OS === 'web' ? 14 : 16),
          lineHeight: fs(20),
        }}>
        {title}
      </Text>
      <Text
        style={{
          color: 'white',
          fontFamily: GTFonts.regular,
          fontSize: fs(12),
          lineHeight: fs(16),
          opacity: 0.95,
        }}>
        {formatLiveHeaderTime(now)}
      </Text>
    </View>
  );
};

const CheckoutCustomerHeaderTitle = () => {
  const customerName = useAppSelector(
    state => state.cart.cartOrderData.customerName,
  );

  return <LiveHeaderTitle title={getHeaderCustomerName(customerName)} />;
};

const HeaderOnlyWebFrame: React.FC<
  WebHeaderBarProps & { children: React.ReactNode }
> = ({ children, ...headerProps }) => {
  const { useSidebarNav } = useLayoutSize();

  if (!useSidebarNav) {
    return <>{children}</>;
  }

  return (
    <View style={{ flex: 1 }}>
      <WebHeaderBar {...headerProps} />
      <View style={{ flex: 1, minWidth: 0 } as any}>{children}</View>
    </View>
  );
};

const ProductScannerHeaderRight: React.FC<{
  navigation: any;
  refreshScanner?: () => void;
}> = ({ navigation, refreshScanner }) => {
  const [menuVisible, setMenuVisible] = useState(false);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
      }}>
      {refreshScanner && (
        <HeaderIconButton
          tooltip="Refresh scanner"
          onPress={refreshScanner}
          style={{ padding: s(4) }}
          accessibilityLabel="Refresh scanner">
          <MaterialCommunityIcons
            name="reload"
            size={iconSize(isTablet() ? 20 : 24, {
              scaleMode: 'height',
            })}
            color="white"
          />
        </HeaderIconButton>
      )}
      <Menu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <HeaderIconButton
            tooltip="Open scanner menu"
            onPress={() => setMenuVisible(true)}
            style={{ padding: s(6) }}
            accessibilityLabel="Open scanner menu">
            <MaterialCommunityIcons
              name="dots-vertical"
              size={iconSize(isTablet() ? 20 : 24, {
                scaleMode: 'height',
              })}
              color="#fff"
            />
          </HeaderIconButton>
        }
        anchorPosition="bottom"
        contentStyle={{ backgroundColor: '#fff' }}>
        <Menu.Item
          leadingIcon="camera"
          onPress={() => {
            setMenuVisible(false);
            navigation.navigate('CameraSettingsScreen');
          }}
          title="Camera Settings"
        />
      </Menu>
    </View>
  );
};

const CheckoutHeaderRight: React.FC<{
  navigation: any;
}> = ({ navigation }) => {
  const { btprint, isPrinting } = useContext(PrintReceiptContext);
  const [menuVisible, setMenuVisible] = useState(false);
  const role =
    useAppSelector(s => s.auth.inApp.account.employeeInfo?.bp_employee_role) ||
    '';
  const isAdmin = isEmployeeAdminRole(role);

  const handlePrint = () => {
    if (isPrinting) return;
    void btprint('print', 'checkout_header');
  };

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
      }}>
      <Pressable
        onPress={handlePrint}
        disabled={isPrinting}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: s(5),
          marginRight: s(4),
          opacity: isPrinting ? 0.5 : 1,
        }}
        accessibilityRole="button"
        accessibilityLabel={isPrinting ? 'Printing receipt' : 'Print receipt'}
        accessibilityState={{ disabled: isPrinting, busy: isPrinting }}>
        <Text
          style={{
            fontFamily: GTFonts.semibold,
            color: 'white',
            fontSize: fs(Platform.OS === 'web' ? 14 : 16),
            lineHeight: fs(isTablet() ? 20 : 18, {
              scaleMode: 'height',
            }),
          }}>
          {isPrinting ? 'Printing' : 'Print'}
        </Text>
        {isPrinting ? (
          <ActivityIndicator color="white" size="small" />
        ) : (
          <Zocial
            name="print"
            size={iconSize(isTablet() ? 22 : 20, {
              scaleMode: 'height',
            })}
            color="white"
          />
        )}
      </Pressable>

      <Menu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <HeaderIconButton
            tooltip="Open checkout menu"
            onPress={() => setMenuVisible(true)}
            style={{ padding: s(6) }}
            accessibilityLabel="Open checkout menu">
            <MaterialCommunityIcons
              name="dots-vertical"
              size={iconSize(isTablet() ? 20 : 24, {
                scaleMode: 'height',
              })}
              color="#fff"
            />
          </HeaderIconButton>
        }
        anchorPosition="bottom">
        <Menu.Item
          leadingIcon="printer"
          onPress={() => {
            setMenuVisible(false);
            navigation.navigate('PrinterSettingsScreen');
          }}
          title="Printer Settings"
        />
        {isAdmin ? (
          <Menu.Item
            leadingIcon="cog"
            onPress={() => {
              setMenuVisible(false);
              navigation.navigate('CheckoutSettingsScreen');
            }}
            title="Checkout Settings"
          />
        ) : null}
      </Menu>
    </View>
  );
};

const getCheckoutCancelOrderData = (order: any[]) => {
  const productinfo = order.map(od => od.bp_product_information);
  const cartItems = order?.map(item => ({
    variantId: item.bp_product_information?.bp_variant_id ?? item.bp_variant_id,
    Quantity: item.bp_order_quantity,
    mass: item.bp_order_mass,
    itemname: item.bp_product_information.bp_product_name,
    unitofmass: item.bp_product_information.bp_product_unit_of_mass,
    price: saleLineUnitPrice(item),
    productID: item.bp_product_information._id,
    size: item.bp_product_information.bp_product_size,
    model: item.bp_product_information.bp_product_model,
    image: item.bp_product_information.bp_product_image,
    quantity: item.bp_product_information.bp_product_quantity,
    price_type: item.bp_product_information.bp_product_price_type,
    bp_order_paid_quantity: item.bp_order_paid_quantity,
    bp_order_paid_mass: item.bp_order_paid_mass,
  }));

  return { productinfo, cartItems };
};

const AppStack: React.FC = () => {
  const {
    width: windowWidth,
    height: windowHeight,
    useSidebarNav: useSidebar,
    useWebLayout,
  } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    windowWidth,
    windowHeight,
  );
  const isTabletCompact = windowWidth >= 768 && windowWidth < 1024;
  const webHeaderSearchMaxWidth = isTabletCompact ? s(660) : s(620);
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(windowWidth * 0.7, windowWidth - s(110))
    : windowWidth * 0.7;
  const { theme, isDark } = useTheme();
  const dispatch = useDispatch();
  const { setRefreshing, receipt } = useContext(ExtrasContext);
  const {
    setOrderSearchId,
    orderSearchId,
    searchNotesQuery,
    setSearchNotesQuery,
  } = useContext(SearchContext);
  const employeeRole =
    useAppSelector(s => s.auth.inApp.account.employeeInfo?.bp_employee_role) ||
    '';
  const currentLocation = useAppSelector(s => s.location.currentLocation);
  const isAdmin = isEmployeeAdminRole(employeeRole);
  // US1: eligible new owners land on the wizard, never Home. Evaluated once
  // at navigator mount (initialRouteName changes are ignored afterwards);
  // eligibility was flushed before Auth→App flipped, so the value is stable.
  const shouldEnterOwnerSetup = useAppSelector(state => {
    const employee = state.auth.inApp.account.employeeInfo;
    const ownerId = employee?.bp_owner_id;
    // Only the registering owner employee enters the wizard — never managers
    // or other staff who share the same bp_owner_id.
    if (!isEmployeeOwnerRole(employee?.bp_employee_role)) return false;
    return (
      selectShouldEnterInitialOwnerSetup(state) &&
      matchesAuthenticatedOwner(state, ownerId ? String(ownerId) : null) &&
      state.ownerActivation.eligibility.sessionBootstrap !== 'pending' &&
      state.ownerActivation.eligibility.sessionBootstrap !== 'requires_login'
    );
  });
  const [ordersDateFilterVisible, setOrdersDateFilterVisible] = useState(false);
  const [ordersDateFilterActive, setOrdersDateFilterActive] = useState(false);
  const [ordersDateFilterStartISO, setOrdersDateFilterStartISO] = useState(
    moment().startOf('month').toISOString(),
  );
  const [ordersDateFilterEndISO, setOrdersDateFilterEndISO] = useState(
    moment().endOf('day').toISOString(),
  );
  // const { business } = useContext(AuthContext);

  const Refresh = () => {
    AppAlert.alert('Refresh Orders', 'This will clear and refresh all orders', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'OK',
        onPress: () => {
          dispatch(clearCompletedOrders());
          dispatch(clearPendingOrders());
          dispatch(clearPartialOrders());
          dispatch(clearFilteredCompletedOrder());
          dispatch(clearFilteredPendingOrder());
          dispatch(clearFilteredPartialOrder());
          setRefreshing((prev: boolean) => !prev);
        },
      },
    ]);
  };

  const handleClearSearch = () => {
    dispatch(clearFilteredCompletedOrder());
    dispatch(clearFilteredPendingOrder());
    dispatch(clearFilteredPartialOrder());
    setOrderSearchId('');
  };

  const handleToggleOrdersDateFilter = useCallback(() => {
    setOrdersDateFilterVisible(value => !value);
  }, []);

  const handleApplyOrdersDateFilter = useCallback(
    (fromISO: string, toISO: string) => {
      setOrderSearchId('');
      dispatch(clearFilteredCompletedOrder());
      dispatch(clearFilteredPendingOrder());
      dispatch(clearFilteredPartialOrder());
      setOrdersDateFilterStartISO(fromISO);
      setOrdersDateFilterEndISO(toISO);
      setOrdersDateFilterActive(true);
      setOrdersDateFilterVisible(false);
    },
    [dispatch, setOrderSearchId],
  );

  const deleteNoteFromStorage = async (noteId: string) => {
    try {
      const notesJson = await AsyncStorage.getItem('notes');
      if (notesJson) {
        let notes = JSON.parse(notesJson);
        notes = notes.filter((note: { id: string }) => note.id !== noteId);
        await AsyncStorage.setItem('notes', JSON.stringify(notes));
        appAnalytics.track('notes_deleted', {
          ...buildAppAnalyticsContext(),
          count_after: notes.length,
        });
      }
    } catch (error) {
      console.error('Error deleting note from AsyncStorage:', error);
    }
  };

  const handleSetOrderSearchId = (text: string) => {
    setOrderSearchId(text.toLowerCase());

    if (text.length === 0) {
      dispatch(clearFilteredCompletedOrder());
      dispatch(clearFilteredPendingOrder());
      dispatch(clearFilteredPartialOrder());
    }
  };

  const webBackOfficeBackToHome = useCallback((navigation: any) => {
    if (Platform.OS !== 'web') {
      navigation.goBack();
      return;
    }
    try {
      // Prefer native-stack popTo when supported (keeps router semantics)
      if (typeof navigation.popTo === 'function') {
        navigation.popTo('BackOffice', { screen: 'Dashboard' });
        return;
      }
    } catch {}
    // Universal fallback: reset the stack to BackOffice Dashboard
    navigation.reset({
      index: 0,
      routes: [{ name: 'BackOffice', params: { screen: 'Dashboard' } }],
    });
  }, []);

  const webEmployeeBackToHome = useCallback((navigation: any) => {
    if (Platform.OS !== 'web') {
      navigation.goBack();
      return;
    }
    try {
      // Prefer native-stack popTo when supported (keeps router semantics)
      if (typeof navigation.popTo === 'function') {
        navigation.popTo('MainScreen', { screen: 'ProductsScreen' });
        return;
      }
    } catch {}
    // Universal fallback: reset the stack to MainScreen ProductsScreen (home)
    navigation.reset({
      index: 0,
      routes: [{ name: 'MainScreen', params: { screen: 'ProductsScreen' } }],
    });
  }, []);

  return (
    <BackOfficeSessionProvider>
      <>
        <NetworkMonitor />
        <Stack.Navigator
          initialRouteName={
            shouldEnterOwnerSetup ? 'OwnerSetupWizard' : 'DrawerNavigation'
          }
          screenOptions={{
            statusBarStyle: 'light',
            statusBarBackgroundColor: '#1560bd',
            headerStyle: {
              backgroundColor: '#1560bd',
            },
            headerLeft: (props: any) => <HeaderLeft {...props} />,
            // ...TransitionPresets.SlideFromRightIOS,
            // cardStyleInterpoLato-Regularr: CardStyleInterpoLato-Regularrs.forHorizontalIOS,
            // transitionSpec: {
            //   open: {
            //     animation: 'timing',
            //     config: {
            //       duration: 160,
            //     },
            //   },
            //   close: {
            //     animation: 'timing',
            //     config: {
            //       duration: 150,
            //     },
            //   },
            // },
          }}>
          <Stack.Screen
            name="DrawerNavigation"
            component={DrawerNavigation}
            options={{
              headerShown: false,
            }}
          />
          <Stack.Screen
            name="OwnerSetupWizard"
            component={OwnerSetupWizardScreen}
            options={{
              headerShown: false,
              // Step 1 must not expose Home/auth history via back gestures;
              // wizard Back rules are handled inside the screen (US4).
              gestureEnabled: false,
              // The wizard paints a white/edge-to-edge page, so override the
              // navigator's default light status bar (white icons on #1560bd)
              // — otherwise the icons are invisible on white and the bar looks
              // "obstructed" by the screen.
              statusBarStyle: 'dark',
              statusBarBackgroundColor: 'transparent',
              statusBarTranslucent: true,
            }}
          />
          <Stack.Screen
            name="Calculator"
            options={({ navigation }) => ({
              headerTitle: 'Calculator',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
              headerStyle: {
                backgroundColor: '#1560bd',
              },
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Calculator' }}>
                <CalculatorWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeIntegrations"
            options={({ navigation }) => ({
              headerTitle: 'Integrations',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    webBackOfficeBackToHome(navigation);
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Integrations',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    webBackOfficeBackToHome(props.navigation);
                  },
                }}>
                <BackOfficeIntegrationsScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeReportPage"
            options={({ navigation, route }) => {
              const reportTitles: Record<string, string> = {
                EMPLOYEES: 'All Employees report',
                BEST_SELLING_PRODUCTS: 'Best Selling Products',
                SALES_REPORT: 'Sales Report',
                YEARLY_SALES_TOTALS: 'Yearly Sales Report',
                DAILY_STOCKS: 'Daily Opening and Closing Stocks',
                MONTHLY_SALES_REPORT: 'Monthly Sales Report',
                UPDATED_PRODUCTS: 'Updated Products Report',
              };
              const params: any = route?.params || {};
              const resolvedTitle =
                params?.title || reportTitles[params?.reportId] || 'Report';
              return {
                headerTitle: resolvedTitle,
                headerShown: !useSidebar,
                headerTintColor: '#fff',
                headerTitleStyle: {
                  fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                  lineHeight: fs(20),
                },
                headerLeft: props => (
                  <HeaderLeft {...props} onPress={() => navigation.goBack()} />
                ),
              };
            }}>
            {(props: any) => {
              const params: any = props.route?.params || {};
              const reportTitles: Record<string, string> = {
                EMPLOYEES: 'All Employees report',
                BEST_SELLING_PRODUCTS: 'Best Selling Products',
                SALES_REPORT: 'Sales Report',
                YEARLY_SALES_TOTALS: 'Yearly Sales Report',
                DAILY_STOCKS: 'Daily Opening and Closing Stocks',
                MONTHLY_SALES_REPORT: 'Monthly Sales Report',
                UPDATED_PRODUCTS: 'Updated Products Report',
              };
              const resolvedTitle =
                params?.title || reportTitles[params?.reportId] || 'Report';
              return (
                <BackOfficeScreenShell header={{ title: resolvedTitle }}>
                  <BackOfficeReportPageWithSuspense {...props} />
                </BackOfficeScreenShell>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="Notifications"
            options={({ navigation }) => ({
              headerTitle: 'Notifications',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Notifications' }}>
                <NotificationsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="Suggestion"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Feedback & Suggestions',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Feedback & Suggestions',
                  onBack: () => props.navigation.goBack(),
                }}>
                <SuggestionScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="MpesaIntegration"
            options={({ navigation }) => ({
              headerTitle: 'M-Pesa Integration',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'M-Pesa Integration' }}>
                <MpesaIntegrationWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="MpesaIntegrationConnect"
            options={({ navigation }) => ({
              headerTitle: 'Connect M-Pesa',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
                fontFamily: GTFonts.semibold,
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Connect M-Pesa' }}>
                <MpesaIntegrationConnectWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="MpesaActivationDetails"
            options={({ navigation }) => ({
              headerTitle: 'Set up M-Pesa',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
                fontFamily: GTFonts.semibold,
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Set up M-Pesa' }}>
                <MpesaActivationDetailsWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="MpesaActivationAuthorise"
            options={({ navigation }) => ({
              headerTitle: 'Authorise BizPlus',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
                fontFamily: GTFonts.semibold,
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Authorise BizPlus' }}>
                <MpesaActivationAuthoriseWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="MpesaPaymentsToCheck"
            options={({ navigation }) => ({
              headerTitle: 'Payments to check',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
                fontFamily: GTFonts.semibold,
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Payments to check' }}>
                <MpesaPaymentsToCheckWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="MpesaPaymentToCheckDetails"
            options={({ navigation }) => ({
              headerTitle: 'Payment Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
                fontFamily: GTFonts.semibold,
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Payment Details' }}>
                <MpesaPaymentToCheckDetailsWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="MainScreen"
            component={MainScreenTabNavigator as React.ComponentType<any>}
            options={{
              headerShown: false,
            }}
            // @ts-ignore
            initialParams={{ refreshFunction: Refresh }}
          />

          <Stack.Screen
            name="ProductScanner"
            options={({
              navigation,
              route,
            }: NativeStackScreenProps<
              RootStackParamList,
              'ProductScanner'
            >) => {
              return {
                headerTitle: 'Scan to Sell',
                headerShown: !useSidebar,
                headerTintColor: '#fff',
                headerTitleStyle: {
                  fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                  lineHeight: fs(20),
                },
                headerLeft: props => (
                  <HeaderLeft
                    {...props}
                    onPress={() => {
                      navigation.goBack();
                    }}
                  />
                ),
                headerRight: () => (
                  <ProductScannerHeaderRight
                    navigation={navigation}
                    refreshScanner={route.params?.refreshScanner}
                  />
                ),
              };
            }}>
            {(props: any) => (
              <HeaderOnlyWebFrame
                title="Scan to Sell"
                onBack={() => props.navigation.goBack()}
                rightSlot={
                  <ProductScannerHeaderRight
                    navigation={props.navigation}
                    refreshScanner={props.route.params?.refreshScanner}
                  />
                }>
                <ProductScannerWithSuspense {...props} />
              </HeaderOnlyWebFrame>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BarcodeCapture"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Scan Barcode',
              headerTintColor: '#fff',
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Scan Barcode',
                  onBack: () => props.navigation.goBack(),
                }}>
                <BarcodeCaptureWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>

          <Stack.Screen
            name="EmployeeSelfie"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Update Profile Photo',
              headerTintColor: '#fff',
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Update Profile Photo',
                  onBack: () => props.navigation.goBack(),
                }}>
                <EmployeeSelfieScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>

          <Stack.Screen
            name="NewLocation"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Add New Business',
              headerTintColor: '#fff',
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Add New Business',
                  onBack: () => props.navigation.goBack(),
                }}>
                <NewLocationWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>

          <Stack.Screen
            name="ServiceDisplay"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Service Display',
              headerTintColor: '#fff',
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Service Display',
                  onBack: () => props.navigation.goBack(),
                }}>
                <ServiceDisplayScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>

          <Stack.Screen
            name="Cart"
            options={({
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'Cart'>) => ({
              headerTitleAlign: 'center',
              headerShown: !useSidebar,
              headerTitle: () => <LiveHeaderTitle title="Basket" />,
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => webEmployeeBackToHome(navigation)}
                />
              ),
              headerRight: () => {
                const CartHeaderRight = () => {
                  const [menuVisible, setMenuVisible] = useState(false);
                  const role =
                    useAppSelector(
                      s => s.auth.inApp.account.employeeInfo?.bp_employee_role,
                    ) || '';
                  const isAdmin = isEmployeeAdminRole(role);
                  if (!isAdmin) return null;
                  return (
                    <View style={{ marginRight: s(4) }}>
                      <Menu
                        visible={menuVisible}
                        onDismiss={() => setMenuVisible(false)}
                        anchor={
                          <HeaderIconButton
                            tooltip="Open basket menu"
                            onPress={() => setMenuVisible(true)}
                            style={{ padding: s(6) }}
                            accessibilityLabel="Open basket menu">
                            <MaterialCommunityIcons
                              name="dots-vertical"
                              size={iconSize(isTablet() ? 20 : 24, {
                                scaleMode: 'height',
                              })}
                              color="#fff"
                            />
                          </HeaderIconButton>
                        }
                        anchorPosition="bottom"
                        contentStyle={{ backgroundColor: '#fff' }}>
                        <Menu.Item
                          leadingIcon="cog"
                          onPress={() => {
                            setMenuVisible(false);
                            navigation.navigate('CartSettingsScreen');
                          }}
                          title="Basket Settings"
                        />
                      </Menu>
                    </View>
                  );
                };
                return <CartHeaderRight />;
              },
              headerTitleStyle: {
                flex: 1,
                color: 'white',
              },
              headerTintColor: 'white',
            })}>
            {(props: any) => {
              const CartWebHeaderRight = () => {
                const [menuVisible, setMenuVisible] = useState(false);
                const role =
                  useAppSelector(
                    s => s.auth.inApp.account.employeeInfo?.bp_employee_role,
                  ) || '';
                const isAdmin = isEmployeeAdminRole(role);
                if (!isAdmin) return null;
                return (
                  <View style={{ marginRight: s(4) }}>
                    <Menu
                      visible={menuVisible}
                      onDismiss={() => setMenuVisible(false)}
                      anchor={
                        <HeaderIconButton
                          tooltip="Open basket menu"
                          onPress={() => setMenuVisible(true)}
                          style={{ padding: s(6) }}
                          accessibilityLabel="Open basket menu">
                          <MaterialCommunityIcons
                            name="dots-vertical"
                            size={iconSize(isTablet() ? 20 : 24, {
                              scaleMode: 'height',
                            })}
                            color="#fff"
                          />
                        </HeaderIconButton>
                      }
                      anchorPosition="bottom"
                      contentStyle={{ backgroundColor: '#fff' }}>
                      <Menu.Item
                        leadingIcon="cog"
                        onPress={() => {
                          setMenuVisible(false);
                          props.navigation.navigate('CartSettingsScreen');
                        }}
                        title="Basket Settings"
                      />
                    </Menu>
                  </View>
                );
              };
              return (
                <WebScreenShell
                  header={{
                    title: <LiveHeaderTitle title="Basket" />,
                    titleAlign: 'center',
                    onBack: () => webEmployeeBackToHome(props.navigation),
                    rightSlot: <CartWebHeaderRight />,
                  }}>
                  <CartWithSuspense {...props} />
                </WebScreenShell>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="CartSettingsScreen"
            options={({ navigation }) => ({
              headerTitle: 'Basket Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Basket Settings' }}>
                <CartSettingsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="Orders"
            options={({
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'Orders'>) => ({
              headerTintColor: '#fff',

              headerShown: !useSidebar,
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  style={{ marginLeft: s(-20) }}
                  onPress={() => webEmployeeBackToHome(navigation)}
                />
              ),
              headerTitle: () => (
                <View
                  style={{
                    flex: 1,
                    maxWidth:
                      Platform.OS === 'web'
                        ? webHeaderSearchMaxWidth
                        : nativeHeaderSearchWidth,
                  }}>
                  <AppSearchBar
                    placeholder="Customer name or Receipt id..."
                    onClear={handleClearSearch}
                    width={
                      Platform.OS === 'web'
                        ? windowWidth * 0.32
                        : nativeHeaderSearchWidth
                    }
                    onChangeText={handleSetOrderSearchId}
                    value={orderSearchId}
                    showLeadingIcon={true}
                    containerStyle={{
                      backgroundColor: '#fff',
                      borderColor: '#fff',
                      borderWidth: 0,
                    }}
                  />
                </View>
              ),
              headerRight: () => (
                <View style={{ marginRight: s(-10) }}>
                  <HeaderActions
                    actions={[
                      {
                        type: 'icon',
                        icon: 'calendar-month-outline',
                        onPress: handleToggleOrdersDateFilter,
                        accessibilityLabel: 'Filter by date',
                      },
                      {
                        type: 'menu',
                        items: [
                          {
                            title: 'Refresh orders',
                            leadingIcon: 'reload',
                            onPress: Refresh,
                          },
                          {
                            title: 'Order Settings',
                            leadingIcon: 'cog-outline',
                            onPress: () =>
                              navigation.navigate(
                                'OrderSettingsScreen' as never,
                              ),
                            visible: isAdmin,
                          },
                        ],
                      },
                    ]}
                  />
                </View>
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  onBack: () => webEmployeeBackToHome(props.navigation),
                  children: (
                    <View
                      style={{
                        flex: 1,
                        maxWidth: webHeaderSearchMaxWidth,
                      }}>
                      <AppSearchBarWeb
                        placeholder="Customer name or Receipt id..."
                        onClear={handleClearSearch}
                        height={v(50)}
                        width={
                          showTabletSidebarLayoutSetting && useSidebar
                            ? windowWidth * 0.46
                            : windowWidth * 0.32
                        }
                        onChangeText={handleSetOrderSearchId}
                        value={orderSearchId}
                        showLeadingIcon={true}
                        containerStyle={{
                          backgroundColor: '#fff',
                          borderColor: '#fff',
                          borderWidth: 0,
                        }}
                      />
                    </View>
                  ),
                  rightSlot: (
                    <HeaderActions
                      actions={[
                        {
                          type: 'icon',
                          icon: 'calendar-month-outline',
                          onPress: handleToggleOrdersDateFilter,
                          accessibilityLabel: 'Filter by date',
                        },
                        {
                          type: 'menu',
                          items: [
                            {
                              title: 'Refresh orders',
                              leadingIcon: 'reload',
                              onPress: Refresh,
                            },
                            {
                              title: 'Order Settings',
                              leadingIcon: 'cog-outline',
                              onPress: () =>
                                props.navigation.navigate(
                                  'OrderSettingsScreen',
                                ),
                              visible: isAdmin,
                            },
                          ],
                        },
                      ]}
                    />
                  ),
                }}>
                <OrdersTabNavigator
                  {...props}
                  dateFilterVisible={ordersDateFilterVisible}
                  dateFilterActive={ordersDateFilterActive}
                  dateFilterStartISO={ordersDateFilterStartISO}
                  dateFilterEndISO={ordersDateFilterEndISO}
                  onApplyDateFilter={handleApplyOrdersDateFilter}
                />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="OrderDetails"
            options={({
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'OrderDetails'>) => ({
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                  <HeaderLeft
                    {...props}
                    onPress={() => webEmployeeBackToHome(navigation)}
                  />
                </View>
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Order Details',
                  onBack: () => webEmployeeBackToHome(props.navigation),
                }}>
                <OrderDetailsWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="Notes"
            options={({ navigation }) => ({
              headerTitle: 'Notes',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Notes',
                  children: (
                    <View
                      style={{
                        flex: 1,
                        maxWidth: webHeaderSearchMaxWidth,
                      }}>
                      <AppSearchBarWeb
                        placeholder="Search notes..."
                        value={searchNotesQuery}
                        onChangeText={setSearchNotesQuery}
                        onClear={() => setSearchNotesQuery('')}
                        showLeadingIcon={true}
                        showBarcodeScan={false}
                        width={
                          showTabletSidebarLayoutSetting && useSidebar
                            ? windowWidth * 0.46
                            : windowWidth * 0.32
                        }
                        height={v(50)}
                        containerStyle={{
                          backgroundColor: '#fff',
                          borderColor: '#fff',
                          borderWidth: 0,
                        }}
                      />
                    </View>
                  ),
                }}>
                <NotesScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="NewNote"
            options={({
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'NewNote'>) => ({
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitle: 'New Note',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                  <HeaderLeft
                    {...props}
                    onPress={() => {
                      if (navigation.canGoBack()) {
                        navigation.goBack();
                      } else {
                        navigation.navigate('Notes');
                      }
                    }}
                  />
                </View>
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'New Note',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                    } else {
                      props.navigation.navigate('Notes');
                    }
                  },
                }}>
                <NewNoteScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="FAQScreen"
            options={({
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'FAQScreen'>) => ({
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitle: 'Frequently Asked Questions',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                  <HeaderLeft
                    {...props}
                    onPress={() => {
                      if (navigation.canGoBack()) {
                        navigation.goBack();
                      } else {
                        navigation.navigate('Notes');
                      }
                    }}
                  />
                </View>
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Frequently Asked Questions',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                    } else {
                      props.navigation.navigate('Notes');
                    }
                  },
                }}>
                <FaqScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="SettingsScreen"
            options={({
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'SettingsScreen'
            >) => ({
              headerTitle: 'Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
              headerRight: () => <HeaderInternetStatusIcon />,
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Settings',
                  rightSlot: <HeaderInternetStatusIcon />,
                }}>
                <SettingsWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="GettingStartedHub"
            options={({
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'GettingStartedHub'
            >) => ({
              headerTitle: 'Getting Started',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
              headerRight: () => <HeaderInternetStatusIcon />,
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Getting Started',
                  rightSlot: <HeaderInternetStatusIcon />,
                }}>
                <GettingStartedHubWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="SecuritySettingsScreen"
            options={({ navigation }) => ({
              headerTitle: 'Security',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Security' }}>
                <SecuritySettingsWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="CameraSettingsScreen"
            options={({
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'CameraSettingsScreen'
            >) => ({
              headerTitle: 'Camera Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Camera Settings' }}>
                <CameraSettingsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="ProductSettingsScreen"
            options={({ navigation }) => ({
              headerTitle: 'Product Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Product Settings' }}>
                <ProductSettingsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="CheckoutSettingsScreen"
            options={({ navigation }) => ({
              headerTitle: 'Checkout Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Checkout Settings' }}>
                <CheckoutSettingsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="OrderSettingsScreen"
            options={({ navigation }) => ({
              headerTitle: 'Order Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Order Settings' }}>
                <OrderSettingsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="ReportSettingsScreen"
            options={({ navigation }) => ({
              headerTitle: 'Report Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Report Settings' }}>
                <ReportSettingsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="PrinterSettingsScreen"
            options={({
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'PrinterSettingsScreen'
            >) => ({
              headerTitle: 'Printer Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
              headerRight: () => (
                <Pressable
                  onPress={() => navigation.navigate('AddPrinterScreen')}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginRight: s(8),
                    padding: s(4),
                  }}
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                  accessibilityRole="button"
                  accessibilityLabel="Add printer">
                  <MaterialIcons name="add" size={iconSize(24)} color="#fff" />
                  <Text
                    style={{
                      color: '#fff',
                      fontSize: fs(13),
                      fontWeight: '700',
                      marginLeft: s(2),
                    }}>
                    Add Printer
                  </Text>
                </Pressable>
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Printer Settings',
                  rightSlot: (
                    <Pressable
                      onPress={() =>
                        props.navigation.navigate('AddPrinterScreen')
                      }
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: s(8),
                        padding: s(4),
                      }}
                      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                      accessibilityRole="button"
                      accessibilityLabel="Add printer">
                      <MaterialIcons
                        name="add"
                        size={iconSize(24)}
                        color="#fff"
                      />
                      <Text
                        style={{
                          color: '#fff',
                          fontSize: fs(13),
                          fontWeight: '700',
                          marginLeft: s(2),
                        }}>
                        Add Printer
                      </Text>
                    </Pressable>
                  ),
                }}>
                <PrinterListScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="AddPrinterScreen"
            options={({
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'AddPrinterScreen'
            >) => ({
              headerTitle: 'Add Printer',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Add Printer' }}>
                <PrinterSettingsScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="EditPrinterScreen"
            options={({
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'EditPrinterScreen'
            >) => ({
              headerTitle: 'Edit Printer',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Edit Printer' }}>
                <PrinterProfileEditorScreenWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="NewProduct"
            options={({
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'NewProduct'>) => ({
              headerShown: !useSidebar,
              headerTitle: 'Add New Product',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 15),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                  <HeaderLeft
                    {...props}
                    onPress={() => {
                      if (navigation.canGoBack()) {
                        navigation.goBack();
                      } else {
                        navigation.navigate('MainScreen', {
                          screen: 'ProductsScreen',
                          params: {
                            from: 'NewProduct',
                          },
                        });
                      }
                    }}
                  />
                </View>
              ),
            })}>
            {(props: any) => (
              <WebScreenShell>
                <NewProductWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="EmployeeAddProductImage"
            // lazy-load component from screens/products/newproducts/EmployeeAddProductImage
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Update Product Image',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Update Product Image',
                  onBack: () => props.navigation.goBack(),
                }}>
                <EmployeeAddProductImageWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="ViewNote"
            options={({
              route,
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'ViewNote'>) => ({
              headerShown: !useSidebar,
              headerTitle: 'Edit',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerRight: () => {
                const noteId = route.params?.noteId;
                return (
                  <HeaderIconButton
                    tooltip="Delete note"
                    onPress={() => {
                      AppAlert.alert(
                        'Delete Note',
                        'Are you sure you want to delete this note?',
                        [
                          { text: 'Cancel', style: 'cancel' },
                          {
                            text: 'Delete',
                            onPress: async () => {
                              try {
                                if (route.params?.setIsDeleting) {
                                  route.params.setIsDeleting(true);
                                }
                                await deleteNoteFromStorage(noteId);
                                navigation.goBack();
                              } catch (error) {
                                console.error(
                                  'Error during note deletion:',
                                  error,
                                );
                                AppAlert.alert(
                                  'Error',
                                  'Failed to delete the note. Please try again.',
                                );
                              }
                            },
                            style: 'destructive',
                          },
                        ],
                      );
                    }}
                    style={{ marginRight: s(15) }}>
                    <MaterialIcons
                      name="delete"
                      size={iconSize(isTablet() ? 24 : 20, {
                        scaleMode: 'height',
                      })}
                      color="white"
                    />
                  </HeaderIconButton>
                );
              },
              headerLeft: props => (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                  <HeaderLeft
                    {...props}
                    onPress={() => {
                      if (navigation.canGoBack()) {
                        navigation.goBack();
                      } else {
                        navigation.navigate('Notes');
                      }
                    }}
                  />
                </View>
              ),
            })}>
            {(props: any) => {
              const noteId = props.route?.params?.noteId;
              return (
                <WebScreenShell
                  header={{
                    title: 'Edit',
                    onBack: () => {
                      if (props.navigation.canGoBack()) {
                        props.navigation.goBack();
                      } else {
                        props.navigation.navigate('Notes');
                      }
                    },
                    rightSlot: (
                      <HeaderIconButton
                        tooltip="Delete note"
                        onPress={() => {
                          AppAlert.alert(
                            'Delete Note',
                            'Are you sure you want to delete this note?',
                            [
                              { text: 'Cancel', style: 'cancel' },
                              {
                                text: 'Delete',
                                onPress: async () => {
                                  try {
                                    if (props.route?.params?.setIsDeleting) {
                                      props.route.params.setIsDeleting(true);
                                    }
                                    await deleteNoteFromStorage(noteId);
                                    props.navigation.goBack();
                                  } catch (error) {
                                    console.error(
                                      'Error during note deletion:',
                                      error,
                                    );
                                    AppAlert.alert(
                                      'Error',
                                      'Failed to delete the note. Please try again.',
                                    );
                                  }
                                },
                                style: 'destructive',
                              },
                            ],
                          );
                        }}
                        style={{ marginRight: s(15) }}>
                        <MaterialIcons
                          name="delete"
                          size={iconSize(isTablet() ? 24 : 20, {
                            scaleMode: 'height',
                          })}
                          color="white"
                        />
                      </HeaderIconButton>
                    ),
                  }}>
                  <ViewNoteScreenWithSuspense {...props} />
                </WebScreenShell>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="Checkout"
            options={({
              route,
              navigation,
            }: NativeStackScreenProps<RootStackParamList, 'Checkout'>) => ({
              headerShown: !useSidebar,
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => {
                const order = route?.params?.order;
                if (!order) return null;
                // console.log('order is ', order);
                const { productinfo, cartItems } =
                  getCheckoutCancelOrderData(order);
                // console.log('productinfo is ', productinfo);
                return (
                  <HeaderLeft
                    {...props}
                    onPress={() => {
                      AppAlert.alert(
                        'Cancel Order ?',
                        'The order will be saved and marked as a pending order',
                        [
                          {
                            text: 'Cancel',
                            onPress: () => null,
                            style: 'cancel',
                          },
                          {
                            text: 'Continue',
                            onPress: () => {
                              appAnalytics.track(
                                'commerce_checkout_cancelled',
                                {
                                  ...buildAppAnalyticsContext({
                                    role: employeeRole,
                                    locationType:
                                      currentLocation?.bp_location_is_system_generated
                                        ? 'test'
                                        : currentLocation
                                          ? 'real'
                                          : undefined,
                                  }),
                                  reason: 'header_back',
                                },
                              );
                              dispatch(setUpdatingCartitems(true));
                              dispatch(addPreviouspendingOrder(cartItems));
                              dispatch(
                                prioritizeProductsByProductObject(productinfo),
                              );
                              navigation.popTo('MainScreen', {
                                screen: 'ProductsScreen',
                                params: {
                                  from: 'Checkout',
                                },
                              });
                            },
                          },
                        ],
                      );
                    }}
                  />
                );
              },
              headerTitleAlign: useWebLayout ? 'center' : 'left',
              headerTitle: () => {
                if (useWebLayout) {
                  return <CheckoutCustomerHeaderTitle />;
                }

                return receipt.ReceiptID ? (
                  <Text
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                      lineHeight: fs(isTablet() ? 20 : 18, {
                        scaleMode: 'height',
                      }),
                    }}>
                    Receipt ID: {receipt.ReceiptID}
                  </Text>
                ) : (
                  <Text
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.regular,
                      fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                      lineHeight: fs(isTablet() ? 20 : 18, {
                        scaleMode: 'height',
                      }),
                    }}>
                    {receipt.ReceiptID}
                  </Text>
                );
              },
              headerRight: () => {
                return <CheckoutHeaderRight navigation={navigation} />;
              },
              headerTintColor: '#fff',
            })}>
            {(props: any) => {
              const order = props.route?.params?.order;
              if (!order) return null;
              const { productinfo, cartItems } =
                getCheckoutCancelOrderData(order);
              const handleCheckoutBack = () => {
                AppAlert.alert(
                  'Cancel Order ?',
                  'The order will be saved and marked as a pending order',
                  [
                    {
                      text: 'Cancel',
                      onPress: () => null,
                      style: 'cancel',
                    },
                    {
                      text: 'Continue',
                      onPress: () => {
                        appAnalytics.track('commerce_checkout_cancelled', {
                          ...buildAppAnalyticsContext({
                            role: employeeRole,
                            locationType:
                              currentLocation?.bp_location_is_system_generated
                                ? 'test'
                                : currentLocation
                                  ? 'real'
                                  : undefined,
                          }),
                          reason: 'header_back',
                        });
                        dispatch(setUpdatingCartitems(true));
                        dispatch(addPreviouspendingOrder(cartItems));
                        dispatch(
                          prioritizeProductsByProductObject(productinfo),
                        );
                        props.navigation.popTo('MainScreen', {
                          screen: 'ProductsScreen',
                          params: {
                            from: 'Checkout',
                          },
                        });
                      },
                    },
                  ],
                );
              };

              return (
                <HeaderOnlyWebFrame
                  title={<CheckoutCustomerHeaderTitle />}
                  titleAlign="center"
                  onBack={handleCheckoutBack}
                  rightSlot={
                    <CheckoutHeaderRight navigation={props.navigation} />
                  }>
                  <CheckoutWithSuspense {...props} />
                </HeaderOnlyWebFrame>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="PartialOrdersCheckoutScreen"
            options={({
              route,
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'PartialOrdersCheckoutScreen'
            >) => ({
              headerShown: !useSidebar,
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => {
                return (
                  <HeaderLeft
                    {...props}
                    onPress={() => {
                      if (navigation.canGoBack()) {
                        navigation.goBack();
                      } else {
                        navigation.navigate('PartialOrdersSelectItems', {
                          order: route.params.order,
                        });
                      }
                    }}
                  />
                );
              },
              headerTitleAlign: useWebLayout ? 'center' : 'left',
              headerTitle: () => {
                if (useWebLayout) {
                  return <CheckoutCustomerHeaderTitle />;
                }

                return receipt.ReceiptID ? (
                  <Text
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                      lineHeight: fs(isTablet() ? 20 : 18, {
                        scaleMode: 'height',
                      }),
                    }}>
                    Receipt ID: {receipt.ReceiptID}
                  </Text>
                ) : (
                  <Text
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                      lineHeight: fs(isTablet() ? 20 : 18, {
                        scaleMode: 'height',
                      }),
                    }}>
                    {receipt.ReceiptID}
                  </Text>
                );
              },
              headerRight: () => {
                return <CheckoutHeaderRight navigation={navigation} />;
              },
              headerTintColor: '#fff',
            })}>
            {(props: any) => {
              const handlePartialCheckoutBack = () => {
                if (props.navigation.canGoBack()) {
                  props.navigation.goBack();
                } else {
                  props.navigation.navigate('PartialOrdersSelectItems', {
                    order: props.route.params.order,
                  });
                }
              };

              return (
                <HeaderOnlyWebFrame
                  title={<CheckoutCustomerHeaderTitle />}
                  titleAlign="center"
                  onBack={handlePartialCheckoutBack}
                  rightSlot={
                    <CheckoutHeaderRight navigation={props.navigation} />
                  }>
                  <PartialOrdersCheckoutScreenWithSuspense {...props} />
                </HeaderOnlyWebFrame>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="PartialOrdersSelectItems"
            options={({
              route,
              navigation,
            }: NativeStackScreenProps<
              RootStackParamList,
              'PartialOrdersSelectItems'
            >) => ({
              headerShown: !useSidebar,
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => {
                return (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 5,
                    }}>
                    <HeaderLeft
                      {...props}
                      onPress={() => {
                        if (navigation.canGoBack()) {
                          navigation.goBack();
                        } else {
                          navigation.navigate('Checkout', {
                            order: route.params.order,
                          });
                        }
                      }}
                    />
                  </View>
                );
              },
              headerTitleAlign: useWebLayout ? 'center' : 'left',
              headerTitle: () => {
                if (useWebLayout) {
                  return <CheckoutCustomerHeaderTitle />;
                }

                return receipt.ReceiptID ? (
                  <Text
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                      lineHeight: fs(isTablet() ? 20 : 18, {
                        scaleMode: 'height',
                      }),
                    }}>
                    Receipt ID: {receipt.ReceiptID}
                  </Text>
                ) : (
                  <Text
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.semibold,
                      fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                      lineHeight: fs(isTablet() ? 20 : 18, {
                        scaleMode: 'height',
                      }),
                    }}>
                    {receipt.ReceiptID}
                  </Text>
                );
              },
              headerRight: () => null,
              headerTintColor: '#fff',
            })}>
            {(props: any) => {
              const handlePartialSelectBack = () => {
                if (props.navigation.canGoBack()) {
                  props.navigation.goBack();
                } else {
                  props.navigation.navigate('Checkout', {
                    order: props.route.params.order,
                  });
                }
              };

              return (
                <HeaderOnlyWebFrame
                  title={<CheckoutCustomerHeaderTitle />}
                  titleAlign="center"
                  onBack={handlePartialSelectBack}>
                  <PartialOrdersSelectItemsWithSuspense {...props} />
                </HeaderOnlyWebFrame>
              );
            }}
          </Stack.Screen>

          <Stack.Screen
            name="QuickActionsSettings"
            options={({ navigation }) => ({
              headerTitle: 'Quick Actions',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell
                header={{
                  title: 'Quick Actions',
                }}>
                <QuickActionsSettingsWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="HomePageSettings"
            options={({ navigation }) => ({
              headerTitle: 'Home Settings',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <WebScreenShell header={{ title: 'Home Settings' }}>
                <HomePageSettingsWithSuspense {...props} />
              </WebScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="NoInternetScreen"
            component={withSuspense(NoInternetScreen)}
            options={{
              headerShown: false,
              presentation: 'modal',
              animation: 'fade',
              statusBarStyle: isDark ? 'light' : 'dark',
              statusBarBackgroundColor: theme.colors.background,
            }}
          />

          {/* Back Office routes */}
          <Stack.Screen
            name="BackOffice"
            component={BackOfficeTabNavigator}
            options={{
              headerShown: false,
            }}
          />
          <Stack.Screen
            name="BackOfficeSummary"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle:
                Platform.OS === 'web'
                  ? () => <BackOfficeHeaderLocationPicker />
                  : 'Summary',
              headerStyle: { backgroundColor: '#1560bd' },
              headerTintColor: '#fff',
              statusBarStyle: 'light',
              statusBarBackgroundColor: '#1560bd',
              headerTitleAlign: Platform.OS === 'web' ? 'center' : 'left',
              headerTitleContainerStyle:
                Platform.OS === 'web'
                  ? { flex: 1, alignItems: 'center' as const }
                  : undefined,
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => webBackOfficeBackToHome(navigation)}
                />
              ),
              headerRight: () => (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginRight: s(8),
                  }}>
                  <View style={{ marginLeft: s(8) }}>
                    <DateRangeDropdown
                      variant="compact"
                      chipBgColor={'rgba(255,255,255,0.15)'}
                      chipTextColor={'#fff'}
                      chipBorderColor={'rgba(255,255,255,0.35)'}
                    />
                  </View>
                </View>
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: <BackOfficeHeaderLocationPicker />,
                  titleAlign: 'center',
                  onBack: () => webBackOfficeBackToHome(props.navigation),
                  rightSlot: (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: s(8),
                      }}>
                      <View style={{ marginLeft: s(8) }}>
                        <DateRangeDropdown
                          variant="compact"
                          chipBgColor={'rgba(255,255,255,0.15)'}
                          chipTextColor={'#fff'}
                          chipBorderColor={'rgba(255,255,255,0.35)'}
                        />
                      </View>
                    </View>
                  ),
                }}>
                <BackOfficeSummaryScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeSupport"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Support',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Support',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeSupportScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeDangerZone"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Danger Zone',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Danger Zone',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeDangerZoneScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeAddEmployee"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Add New Employee',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Add New Employee',
                  onBack: () => props.navigation.goBack(),
                }}>
                <BackOfficeAddEmployeeScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeAddProduct"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Add New Product',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 15),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell>
                <BackOfficeAddProductScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeSuppliers"
            options={({ navigation }) => ({
              headerTitle: 'Suppliers',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Suppliers' }}>
                <BackOfficeSuppliersScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeAddSupplier"
            options={({ navigation, route }) => {
              const params = route.params as
                | { supplierId?: string }
                | undefined;
              const isEdit = Boolean(params?.supplierId);
              return {
                headerTitle: isEdit ? 'Edit Supplier' : 'Add Supplier',
                headerShown: !useSidebar,
                headerTintColor: '#fff',
                headerTitleStyle: {
                  fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                  lineHeight: fs(20),
                },
                headerLeft: props => (
                  <HeaderLeft {...props} onPress={() => navigation.goBack()} />
                ),
              };
            }}>
            {(props: any) => {
              const params = props.route?.params as
                | { supplierId?: string }
                | undefined;
              const isEdit = Boolean(params?.supplierId);
              return (
                <BackOfficeScreenShell
                  header={{
                    title: isEdit ? 'Edit Supplier' : 'Add Supplier',
                  }}>
                  <BackOfficeAddSupplierScreen {...props} />
                </BackOfficeScreenShell>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeExpenseCategories"
            options={({ navigation }) => ({
              headerTitle: 'Select Expense Category',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{ title: 'Select Expense Category' }}>
                <BackOfficeExpenseCategoriesWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeAddExpense"
            options={({ navigation }) => ({
              headerTitle: 'Add Expense',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Add Expense' }}>
                <BackOfficeAddExpenseWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeExpenseDetail"
            options={({ navigation }) => ({
              headerTitle: 'Expense Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Expense Details' }}>
                <BackOfficeExpenseDetailWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeLatestExpenses"
            options={({ navigation }) => ({
              headerTitle: 'Latest Expenses',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Latest Expenses' }}>
                <BackOfficeLatestExpensesWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficePaymentBreakdown"
            options={({ navigation }) => ({
              headerTitle: 'Payments Breakdown',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Payments Breakdown' }}>
                <BackOfficePaymentBreakdownWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeEmployees"
            options={({ navigation }) => ({
              headerTitle: 'Employees',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Employees',
                  onBack: () => {
                    if (Platform.OS !== 'web') {
                      props.navigation.goBack();
                      return;
                    }
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeEmployeesScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeCustomers"
            options={({ navigation }) => ({
              headerTitle: 'Customers',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Customers' }}>
                <BackOfficeCustomersScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeAddCustomer"
            options={({ navigation, route }) => {
              const params = route.params as
                | { customerId?: string }
                | undefined;
              const isEdit = Boolean(params?.customerId);
              return {
                headerTitle: isEdit ? 'Edit Customer' : 'Add Customer',
                headerShown: !useSidebar,
                headerTintColor: '#fff',
                headerTitleStyle: {
                  fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                  lineHeight: fs(20),
                },
                headerLeft: props => (
                  <HeaderLeft {...props} onPress={() => navigation.goBack()} />
                ),
              };
            }}>
            {(props: any) => {
              const params = props.route?.params as
                | { customerId?: string }
                | undefined;
              const isEdit = Boolean(params?.customerId);
              return (
                <BackOfficeScreenShell
                  header={{
                    title: isEdit ? 'Edit Customer' : 'Add Customer',
                    onBack: () => props.navigation.goBack(),
                  }}>
                  <BackOfficeAddCustomerScreenWithSuspense {...props} />
                </BackOfficeScreenShell>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeReports"
            options={({ navigation }) => ({
              headerTitle:
                Platform.OS === 'web'
                  ? () => <BackOfficeHeaderLocationPicker />
                  : 'Reports',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleAlign: Platform.OS === 'web' ? 'center' : 'left',
              headerTitleContainerStyle:
                Platform.OS === 'web'
                  ? { flex: 1, alignItems: 'center' as const }
                  : undefined,
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
              headerRight: () => null,
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: <BackOfficeHeaderLocationPicker />,
                  titleAlign: 'center',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeReportsScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeCompanyProfile"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Business Profile',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Business Profile',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeCompanyProfileScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeUserProfile"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'User Profile',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'User Profile',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeUserProfileScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeProfilePhoto"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Update Profile Photo',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Update Profile Photo',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeProfilePhotoScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeBilling"
            options={({ navigation }) => ({
              headerShown: !useSidebar,
              headerTitle: 'Billing',
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    if (Platform.OS !== 'web') {
                      navigation.goBack();
                      return;
                    }
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                      return;
                    }
                    navigation.navigate('BackOffice', { screen: 'Account' });
                  }}
                />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: 'Billing',
                  onBack: () => {
                    if (props.navigation.canGoBack()) {
                      props.navigation.goBack();
                      return;
                    }
                    props.navigation.navigate('BackOffice', {
                      screen: 'Account',
                    });
                  },
                }}>
                <BackOfficeBillingScreenWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>

          <Stack.Screen
            name="BackOfficeEmployeeView"
            options={({ navigation }) => ({
              headerTitle: 'Employee Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Employee Details' }}>
                <BackOfficeEmployeeViewWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeEmployeeDetails"
            options={({ navigation }) => ({
              headerTitle: 'Edit Employee',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Edit Employee' }}>
                <BackOfficeEmployeeDetailsWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeCustomerDetails"
            options={({ navigation }) => ({
              headerTitle: 'Customer Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Customer Details' }}>
                <BackOfficeCustomerDetailsWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeCustomerOrders"
            options={({ navigation }) => ({
              headerTitle: 'Customer Orders',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Customer Orders' }}>
                <BackOfficeCustomerOrdersWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeProductView"
            options={({ navigation }) => ({
              headerTitle: 'Product Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Product Details' }}>
                <BackOfficeProductViewWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeVariantView"
            options={({ navigation }) => ({
              headerTitle: 'Variant Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Variant Details' }}>
                <BackOfficeVariantViewWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeStockValue"
            options={({ navigation, route }) => ({
              headerTitle: getStockValueTitle(route.params?.filters),
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{
                  title: getStockValueTitle(props.route?.params?.filters),
                }}>
                <BackOfficeStockValueWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeProductDetails"
            options={({ navigation }) => ({
              headerTitle: 'Edit Product',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Edit Product' }}>
                <BackOfficeProductDetailsWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeOrderDetails"
            options={({ navigation }) => ({
              headerTitle: 'Order Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Order Details' }}>
                <BackOfficeOrderDetailsWithSuspense {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeSupplierDetails"
            options={({ navigation }) => ({
              headerTitle: 'Supplier Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Supplier Details' }}>
                <BackOfficeSupplierDetailsScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficePurchaseOrderUpsert"
            options={({ navigation, route }) => ({
              headerTitle: (route?.params as any)?.purchaseOrderId
                ? 'Edit Purchase Order'
                : 'Create Purchase Order',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => {
              const poTitle = (props.route?.params as any)?.purchaseOrderId
                ? 'Edit Purchase Order'
                : 'Create Purchase Order';
              return (
                <BackOfficeScreenShell header={{ title: poTitle }}>
                  <BackOfficePurchaseOrderUpsertScreen {...props} />
                </BackOfficeScreenShell>
              );
            }}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficePurchaseOrderDetails"
            options={({ navigation }) => ({
              headerTitle: 'Purchase Order Details',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell
                header={{ title: 'Purchase Order Details' }}>
                <BackOfficePurchaseOrderDetailsScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeAddInvoice"
            options={({ navigation }) => ({
              headerTitle: 'Add Invoice',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Add Invoice' }}>
                <BackOfficeAddInvoiceScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>
          <Stack.Screen
            name="BackOfficeCreateGoodsReceipt"
            options={({ navigation }) => ({
              headerTitle: 'Create Goods Receipt',
              headerShown: !useSidebar,
              headerTintColor: '#fff',
              headerTitleStyle: {
                fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                lineHeight: fs(20),
              },
              headerLeft: props => (
                <HeaderLeft {...props} onPress={() => navigation.goBack()} />
              ),
            })}>
            {(props: any) => (
              <BackOfficeScreenShell header={{ title: 'Create Goods Receipt' }}>
                <BackOfficeCreateGoodsReceiptScreen {...props} />
              </BackOfficeScreenShell>
            )}
          </Stack.Screen>

          {/* <Stack.Screen
        name="Browser"
        component={Browser}
        options={{
          headerTintColor: '#fff',
          headerShown: false,
          headerStyle: {
            backgroundColor: '#1560bd',
          },
        }}
      /> */}
        </Stack.Navigator>
      </>
    </BackOfficeSessionProvider>
  );
};

export default AppStack;
